import React, { useState } from "react";
import { Draggable } from "react-beautiful-dnd";
import { makeStyles } from "@material-ui/core/styles";
import { 
  Card, 
  CardContent, 
  Typography, 
  IconButton, 
  Menu,
  MenuItem,
  Avatar,
  Chip,
  Tooltip
} from "@material-ui/core";
import { 
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AccessTime as ClockIcon,
  Message as MessageRounded,
  Chat as ChatIcon
} from "@material-ui/icons";
import { toast } from "react-toastify";
import moment from "moment";

import EditCardDialog from "./EditCardDialog";
import ConfirmationDialog from "../ConfirmationDialog";
import CardDetailsDialog from "./CardDetailsDialog";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles(theme => ({
  card: {
    padding: theme.spacing(),
    marginBottom: theme.spacing(1),
    backgroundColor: "#fff",
    borderRadius: 4,
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24)",
    transition: "all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)",
    cursor: "pointer",
    "&:hover": {
      boxShadow: "0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23)",
    },
  },
  title: {
    fontWeight: "bold",
    marginBottom: theme.spacing(1),
  },
  tagContainer: {
    display: "flex",
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
    "& > *": {
      margin: theme.spacing(0.5),
    },
  },
  priorityHigh: {
    backgroundColor: theme.palette.error.light,
  },
  priorityMedium: {
    backgroundColor: theme.palette.warning.light,
  },
  priorityLow: {
    backgroundColor: theme.palette.success.light,
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: theme.spacing(1),
  },
  messageCount: {
    display: "flex",
    alignItems: "center",
  },
  userAssigned: {
    display: "flex",
    alignItems: "center",
  },
}));

const KanbanCard = ({ card, index, onUpdate }) => {
  const classes = useStyles();
  
  const [anchorEl, setAnchorEl] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (event) => {
    if (event) event.stopPropagation();
    setAnchorEl(null);
  };

  const handleEditClick = (event) => {
    if (event) event.stopPropagation();
    handleMenuClose(event);
    setEditOpen(true);
  };

  const handleDeleteClick = (event) => {
    if (event) event.stopPropagation();
    handleMenuClose(event);
    setDeleteConfirmOpen(true);
  };

  const handleCardClick = () => {
    setDetailsOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
    if (onUpdate) onUpdate();
  };

  const handleDetailsClose = () => {
    setDetailsOpen(false);
    if (onUpdate) onUpdate();
  };

  const handleCardDelete = async () => {
    try {
      await api.delete(`/kanban/cards/${card.id}`);
      
      toast.success(i18n.t("kanban.notifications.cardDeleted"));
      
      if (onUpdate) onUpdate();
    } catch (err) {
      console.error(err);
      toast.error(i18n.t("kanban.notifications.cardDeleteError"));
    } finally {
      setDeleteConfirmOpen(false);
    }
  };

  const getPriorityClass = (priority) => {
    switch (priority) {
      case "high":
        return classes.priorityHigh;
      case "medium":
        return classes.priorityMedium;
      case "low":
        return classes.priorityLow;
      default:
        return "";
    }
  };

  // Função para obter o rótulo traduzido para a prioridade
  const getPriorityLabel = (priority) => {
    switch (priority) {
      case "low":
        return i18n.t("kanban.priorities.low");
      case "high":
        return i18n.t("kanban.priorities.high");
      case "medium":
      default:
        return i18n.t("kanban.priorities.medium");
    }
  };

  const getDueDateColor = () => {
    if (!card.dueDate) return "";
    
    const today = moment().startOf('day');
    const dueDate = moment(card.dueDate).startOf('day');
    
    if (dueDate.isBefore(today)) {
      return classes.dueDateLate;
    } else if (dueDate.isSame(today)) {
      return classes.dueDateToday;
    }
    
    return "";
  };

  return (
    <>
      <Draggable draggableId={`card-${card.id}`} index={index}>
        {(provided, snapshot) => (
          <Card
            className={classes.card}
            ref={provided.innerRef}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            elevation={snapshot.isDragging ? 3 : 1}
            onClick={handleCardClick}
          >
            <CardContent>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography className={classes.title} variant="body1">
                  {card.title}
                </Typography>
                <IconButton 
                  className={classes.cardMenuButton} 
                  onClick={handleMenuOpen}
                  aria-label="more"
                  aria-controls="card-menu"
                  aria-haspopup="true"
                  size="small"
                >
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </div>
              
              {card.description && (
                <Typography variant="body2" color="textSecondary" noWrap>
                  {card.description}
                </Typography>
              )}
              
              <div className={classes.tagContainer}>
                {card.priority && (
                  <Chip
                    size="small"
                    label={getPriorityLabel(card.priority)}
                    className={getPriorityClass(card.priority)}
                  />
                )}
                
                {card.tags && card.tags.split(",").map((tag, i) => (
                  <Chip key={i} size="small" label={tag.trim()} />
                ))}
              </div>
              
              <div className={classes.footer}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  {card.ticket && (
                    <Tooltip title={i18n.t("chat.openChat") || "Abrir chat"}>
                      <IconButton 
                        size="small" 
                        onClick={(e) => {
                          e.stopPropagation();
                          window.location.href = `/tickets/${card.ticket.id}`;
                        }}
                        style={{ marginRight: 4 }}
                      >
                        <ChatIcon fontSize="small" color="primary" />
                      </IconButton>
                    </Tooltip>
                  )}
                  
                  {card.messagesCount > 0 && (
                    <div className={classes.messageCount}>
                      <MessageRounded fontSize="small" color="primary" />
                      <Typography variant="caption" style={{ marginLeft: 4 }}>
                        {card.messagesCount}
                      </Typography>
                    </div>
                  )}
                </div>
                
                {card.user && (
                  <div className={classes.userAssigned}>
                 <Typography variant="caption" style={{ marginLeft: 4 }}>
                      {card.user.name ? card.user.name.split(' ')[0] : i18n.t("kanban.noResponsible")}
                    </Typography>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </Draggable>
      
      <EditCardDialog
        open={editOpen}
        onClose={handleEditClose}
        card={card}
      />
      
      <CardDetailsDialog
        open={detailsOpen}
        onClose={handleDetailsClose}
        card={card}
      />
      
      <ConfirmationDialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        onConfirm={handleCardDelete}
        title={i18n.t("kanban.dialogs.deleteCard.title")}
        message={i18n.t("kanban.dialogs.deleteCard.message")}
      />
      
      <Menu
        id="card-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditClick}>{i18n.t("kanban.actions.editCard")}</MenuItem>
        <MenuItem onClick={handleDeleteClick}>{i18n.t("kanban.actions.deleteCard")}</MenuItem>
      </Menu>
    </>
  );
};

export default KanbanCard;